import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CREATOR } from "@/constants/testIds";
import ProductCard from "@/components/ProductCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareButton from "@/components/ShareButton";
import { UserCircle2 } from "lucide-react";

export default function CreatorProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/creators/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 font-mono text-xs text-white/50">Loading…</div>;
  if (notFound || !data) return <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 font-mono text-xs text-white/50">Creator not found</div>;

  const { creator, stats, products } = data;

  return (
    <div data-testid={CREATOR.page} className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
      <div className="border border-white/10 bg-[#0A0A0A] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-2 flex items-start justify-start">
          {creator.picture ? (
            <img src={creator.picture} alt={creator.name} className="w-24 h-24 object-cover border border-white/20" />
          ) : (
            <div className="w-24 h-24 border border-white/20 bg-[#141414] flex items-center justify-center">
              <UserCircle2 className="w-12 h-12 text-white/40" />
            </div>
          )}
        </div>
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00]">// Creator</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <h1 data-testid={CREATOR.name} className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1]">
              {creator.name}
            </h1>
            {creator.is_verified && <VerifiedBadge testId={CREATOR.verifiedBadge} size="lg" />}
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              Joined {new Date(creator.joined_at).toLocaleDateString(undefined, { year: "numeric", month: "short" })}
            </div>
            <ShareButton
              testId="creator-share-btn"
              url={`${window.location.origin}/creator/${creator.user_id}`}
              shareUrl={`${window.location.origin}/api/share/creator/${creator.user_id}`}
              title={`${creator.name} on Digital Drops`}
              description={`${stats.total_drops} drops · ${stats.total_sold} sold`}
            />
          </div>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 gap-3">
          <StatBox testId={CREATOR.totalDrops} label="Drops" value={stats.total_drops} />
          <StatBox testId={CREATOR.totalSold} label="Sold" value={stats.total_sold} />
        </div>
      </div>

      <div className="mt-12">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
          // Active drops
        </div>
        {products.length === 0 ? (
          <div className="border border-dashed border-white/15 py-16 text-center text-white/60">
            No active drops yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>

      <div className="mt-14">
        <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-white/70">
          ← Back to store
        </Link>
      </div>
    </div>
  );
}

function StatBox({ label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-white/10 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-mono text-2xl text-[#CCFF00] mt-1">{value}</div>
    </div>
  );
}
